import {
	forwardRef,
	useEffect,
	useId,
	useImperativeHandle,
	useState
} from "react";
import { MentionItem } from "./mentionSuggestions";

export interface MentionListProps {
	items: MentionItem[];
	command: (item: { id: string; label: string }) => void;
	isLoading?: boolean;
}

export interface MentionListRef {
	onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const MentionList = forwardRef<MentionListRef, MentionListProps>(
	({ items, command, isLoading }, ref) => {
		const [selectedIndex, setSelectedIndex] = useState(0);
		useEffect(() => setSelectedIndex(0), [items]);

		const listboxId = useId();
		const optionId = (index: number) => `${listboxId}-option-${index}`;

		const select = (index: number) => {
			const item = items[index];
			if (item) command({ id: item.id, label: item.label });
		};

		useImperativeHandle(ref, () => ({
			onKeyDown: ({ event }) => {
				// FIX A: guard against empty list — pass key through unchanged
				if (items.length === 0) return false;

				if (event.key === "ArrowUp") {
					setSelectedIndex((i) => (i + items.length - 1) % items.length);
					return true;
				}
				if (event.key === "ArrowDown") {
					setSelectedIndex((i) => (i + 1) % items.length);
					return true;
				}
				if (event.key === "Enter") {
					select(selectedIndex);
					return true;
				}
				return false;
			}
		}));

		// FIX B: role="status" + aria-live="polite" on loading/empty states
		if (isLoading)
			return (
				<div
					role="status"
					aria-live="polite"
					className="px-3 py-2 text-sm text-muted-foreground"
				>
					Searching…
				</div>
			);
		if (items.length === 0)
			return (
				<div
					role="status"
					aria-live="polite"
					className="px-3 py-2 text-sm text-muted-foreground"
				>
					No matches
				</div>
			);

		// FIX C: listbox/option semantics
		// - listbox has aria-label and id
		// - aria-activedescendant points to the currently-selected option
		// - each option is a div[role="option"] with a stable id
		// NOTE: full combobox wiring (aria-activedescendant on the focused
		// contenteditable itself) is out of scope here — focus deliberately
		// stays in the TipTap editor while this popup is open, so the
		// activedescendant relationship cannot be completed without changes
		// across the TipTap extension boundary.
		return (
			<div
				id={listboxId}
				role="listbox"
				aria-label="Mention suggestions"
				aria-activedescendant={
					items[selectedIndex] ? optionId(selectedIndex) : undefined
				}
				className="bg-white border border-border rounded-lg shadow-md max-h-60 overflow-y-auto w-64"
			>
				{items.map((item, index) => (
					<div
						key={item.id}
						id={optionId(index)}
						role="option"
						aria-selected={index === selectedIndex}
						tabIndex={-1}
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => select(index)}
						className={`w-full text-left px-3 py-2 text-sm ${index === selectedIndex ? "bg-primary-lighter" : "hover:bg-primary-lighter"}`}
					>
						<span className="font-medium">{item.label}</span>
						{item.subtitle ? (
							<span className="block text-xs text-muted-foreground">
								{item.subtitle}
							</span>
						) : null}
					</div>
				))}
			</div>
		);
	}
);

MentionList.displayName = "MentionList";
export default MentionList;
